import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { theme } from "../theme.js";
import LoadingOverlay from "../components/LoadingOverlay.jsx";

const API_URL = process.env.REACT_APP_API_URL;
const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

export default function Payment() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.state?.editMode || false;
  const isTransferMode = location.state?.transferMode || false;
  const transfer_id = location.state?.transfer_id || null;
  const { event_id, race_id, price_id } = useParams();
  const cardElementRef = useRef(null);
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardElement, setCardElement] = useState(null);

  const [price, setPrice] = useState(null);
  const [race, setRace] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [stripeFees, setStripeFees] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    cardName: "",
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Initialize Stripe - separate from data fetching
  useEffect(() => {
    if (!window.Stripe) {
      console.error("Stripe.js not loaded");
      return;
    }

    if (!STRIPE_PUBLIC_KEY) {
      console.error("Stripe public key not found");
      return;
    }

    try {
      const stripeInstance = window.Stripe(STRIPE_PUBLIC_KEY);
      setStripe(stripeInstance);

      const elementsInstance = stripeInstance.elements();
      setElements(elementsInstance);

      console.log("Stripe initialized successfully");
    } catch (error) {
      console.error("Stripe initialization error:", error);
    }
  }, []);

  // Mount card element after it's DOM is ready
  useEffect(() => {
    if (!elements) {
      console.log("Elements not ready yet");
      return;
    }

    if (cardElement) {
      console.log("Card element already mounted");
      return;
    }

    // Function to attempt mounting the card
    const attemptMount = () => {
      const cardContainer = document.getElementById("card-element");
      
      if (!cardContainer) {
        console.log("⏳ Card container not yet in DOM, retrying in 200ms...");
        // Retry after a short delay
        setTimeout(attemptMount, 200);
        return;
      }

      console.log("✓ Card container found in DOM, mounting Stripe card element");

      try {
        const newCard = elements.create("card", {
          style: {
            base: {
              fontSize: "16px",
              color: "#424770",
              "::placeholder": {
                color: "#aab7c4",
              },
            },
            invalid: {
              color: "#fa755a",
            },
          },
        });

        newCard.mount("#card-element");
        setCardElement(newCard);

        newCard.on("change", (event) => {
          if (event.error) {
            console.error("Card error:", event.error.message);
            setCardError(event.error.message);
          } else {
            setCardError("");
          }
        });

        console.log("✓ Card element mounted successfully");

        return () => {
          newCard.unmount();
        };
      } catch (error) {
        console.error("❌ Card element mount error:", error);
        setCardError("Failed to load card element: " + error.message);
      }
    };

    // Start the mounting process
    attemptMount();
  }, [elements, cardElement]);

  // Fetch data and calculate real fees
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Payment params:", { event_id, race_id, price_id, isEditMode });

        // Validate required parameters
        if (!race_id) {
          setCardError("Missing race information. Please go back and select a race.");
          setLoading(false);
          return;
        }

        // Get registration data from localStorage
        const storedRegistration = localStorage.getItem("registrationData");
        if (storedRegistration) {
          const data = JSON.parse(storedRegistration);
          setRegistrationData(data);
          setFormData((prev) => ({
            ...prev,
            email: data.email || "",
            cardName: `${data.firstname || ""} ${data.lastname || ""}`.trim(),
          }));
        }

        // In edit mode or transfer mode, skip price fetch and only show option changes
        if (isEditMode || isTransferMode) {
          const modeLabel = isTransferMode ? "Transfer mode" : "Edit mode";
          console.log(`${modeLabel} detected - skipping price lookup`);
          
          // Fetch race info
          const raceRes = await fetch(`${API_URL}/api/races/${race_id}`);
          
          if (!raceRes.ok) {
            const errorData = await raceRes.json().catch(() => ({}));
            throw new Error(`Failed to fetch race: ${raceRes.status} - ${errorData.detail || raceRes.statusText}`);
          }

          const raceData = await raceRes.json();
          setRace(raceData);
          
          // Calculate fees based only on new options
          const optionsSubtotal = calculateOptionsSubtotal(storedRegistration ? JSON.parse(storedRegistration) : null);
          console.log(`${modeLabel} - optionsSubtotal: ${optionsSubtotal}`);
          
          // Only charge if there are new options with cost
          if (optionsSubtotal > 0) {
            const stripePercentageFee = 0.025; // 2.5%
            const stripeFixedFee = 0.30; // €0.30
            
            const rawFee = (optionsSubtotal + stripeFixedFee) / (1 - stripePercentageFee) - optionsSubtotal;
            const total_fee = Math.ceil(rawFee * 100) / 100;
            const total_amount = Math.round((optionsSubtotal + total_fee) * 100) / 100;
            
            setStripeFees({
              subtotal: optionsSubtotal,
              stripe_percentage_fee: Math.ceil((optionsSubtotal * stripePercentageFee)*100)/100,
              stripe_fixed_fee: stripeFixedFee,
              total_fee: total_fee,
              total_amount: total_amount,
              currency: "EUR"
            });
          } else {
            // No additional cost, set minimal fees
            setStripeFees({
              subtotal: 0,
              stripe_percentage_fee: 0,
              stripe_fixed_fee: 0,
              total_fee: 0,
              total_amount: 0,
              currency: "EUR"
            });
          }
          
          // Create a placeholder price object
          const priceLabel = isTransferMode ? "Transfer - Options" : "Option Update";
          setPrice({ price: 0, name: priceLabel });
        } else {
          // Normal registration flow - fetch price
          if (!price_id) {
            setCardError("Missing price information. Please go back and select a price.");
            setLoading(false);
            return;
          }

          const priceRes = await fetch(`${API_URL}/api/prices/${price_id}`);
          
          if (!priceRes.ok) {
            const errorData = await priceRes.json().catch(() => ({}));
            throw new Error(`Failed to fetch price: ${priceRes.status} - ${errorData.detail || priceRes.statusText}`);
          }

          const priceData = await priceRes.json();
          setPrice(priceData);

          // Fetch race info
          const raceRes = await fetch(`${API_URL}/api/races/${race_id}`);
          
          if (!raceRes.ok) {
            const errorData = await raceRes.json().catch(() => ({}));
            throw new Error(`Failed to fetch race: ${raceRes.status} - ${errorData.detail || raceRes.statusText}`);
          }

          const raceData = await raceRes.json();
          setRace(raceData);

          // Calculate Stripe fees locally
          const subtotal = calculateSubtotal(priceData, storedRegistration ? JSON.parse(storedRegistration) : null);
          
          // Stripe fees: 2.5% + €0.30
          const stripePercentageFee = 0.025; // 2.5%
          const stripeFixedFee = 0.30; // €0.30
          
          // Ceil the fee first, then total = subtotal + ceiled_fee (so they always add up)
          const rawFee = (subtotal + stripeFixedFee) / (1 - stripePercentageFee) - subtotal;
          const total_fee = Math.ceil(rawFee * 100) / 100;
          const total_amount = Math.round((subtotal + total_fee) * 100) / 100;
          
          setStripeFees({
            subtotal: subtotal,
            stripe_percentage_fee: Math.ceil((subtotal * stripePercentageFee)*100)/100,
            stripe_fixed_fee: stripeFixedFee,
            total_fee: total_fee,
            total_amount: total_amount,
            currency: "EUR"
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setCardError(error.message || "Failed to load payment data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [price_id, race_id, isEditMode, isTransferMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateSubtotal = (priceData = price, regData = registrationData) => {
    let total = priceData?.price || 0;
    if (regData?.extraOptions) {
      Object.entries(regData.extraOptions).forEach(([key, value]) => {
        const optionPrice = value?.price || 0;
        total += parseFloat(optionPrice);
      });
    }
    return total;
  };

  const calculateOptionsSubtotal = (regData = registrationData) => {
    let total = 0;
    if (regData?.extraOptions) {
      Object.entries(regData.extraOptions).forEach(([key, value]) => {
        const optionPrice = value?.price || 0;
        total += parseFloat(optionPrice);
      });
    }
    return total;
  };

  const calculateTotal = (includeStripefees = false) => {
    const subtotal = calculateSubtotal();
    
    if (includeStripefees && stripeFees) {
      return stripeFees.total_amount;
    }
    
    return subtotal;
  };
  
  const calculateStripeFees = () => {
    if (stripeFees) {
      return stripeFees.total_fee;
    }
    return 0;
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !cardElement) {
      setCardError("Payment form not ready. Please try again in a moment.");
      return;
    }

    if (!formData.email || !formData.cardName) {
      setCardError("Please fill in all required fields");
      return;
    }

    setProcessing(true);
    setCardError(null);

    try {
      if (!race_id || !price_id) {
        throw new Error("Missing race or price information");
      }

      console.log("Starting payment process...", { race_id, price_id });

      // Step 1: Create payment intent (without order_id first)
      console.log("Creating payment intent...");
      const paymentIntentRes = await fetch(
        `${API_URL}/api/stripe/create-payment-intent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount: calculateTotal(true), // Include Stripe fees
            currency: "eur",
          }),
        }
      );

      if (!paymentIntentRes.ok) {
        const error = await paymentIntentRes.text();
        throw new Error(`Failed to create payment intent: ${error}`);
      }

      const { clientSecret } = await paymentIntentRes.json();

      console.log("Payment intent created");

      // Step 2: Confirm payment with Stripe FIRST
      console.log("Confirming payment with Stripe...");
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: formData.email,
              name: formData.cardName,
            },
          },
        }
      );

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      // Step 2: Verify payment is accepted
      console.log(`Payment status from Stripe: ${paymentIntent.status}`);
      
      if (paymentIntent.status !== "succeeded") {
        setCardError(`Payment was not accepted. Status: ${paymentIntent.status}`);
        setProcessing(false);
        return;
      }
      
      console.log("✓ Payment accepted by Stripe!");

      // Step 3: Create registration ONLY AFTER payment is validated
      console.log("Step 3: Creating registration after payment validation...");
      
      // Get user_id from localStorage if user is logged in
      let userId = null;
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          userId = user?.id || user?.user_id || null;
          console.log("User ID from localStorage:", userId);
        } catch (e) {
          console.error("Failed to parse user from localStorage:", e);
        }
      }
      
      const registerRes = await fetch(`${API_URL}/api/registrations`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            race_id: race_id,
            ...(isEditMode ? {} : { price_id: price_id }),
            ...(isTransferMode && transfer_id ? { transfer_id: transfer_id } : {}),
            ...registrationData,
            user_id: userId,
            cardName: formData.cardName,
            cardEmail: formData.email,
          }),
        });

        if (!registerRes.ok) {
          const error = await registerRes.text();
          throw new Error(`Registration failed: ${error}`);
        }

        const registerData = await registerRes.json();
        const orderId = registerData.order_id;
        const bibId = registerData.bib_id;

        console.log("Registration created:", orderId, "Bib ID:", bibId);

        if (!orderId) {
          throw new Error("No order_id returned from registration");
        }

        // Step 4: Confirm payment on backend with order_id
        console.log("Step 4: Confirming payment on backend...");
        const confirmRes = await fetch(`${API_URL}/api/stripe/confirm-payment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            provider_payment_id: paymentIntent.id,
            order_id: orderId,
            amount: calculateTotal(true),
            subtotal: calculateSubtotal(),
            payment_fee: calculateStripeFees()
          }),
        });

        if (confirmRes.ok) {
          localStorage.removeItem("registrationData");
          navigate(`/tickets/${bibId}`);
        } else {
          const error = await confirmRes.text();
          throw new Error(`Failed to confirm payment: ${error}`);
        }
    } catch (error) {
      console.error("Payment error:", error);
      setCardError(error.message || "Paiement échoué. Veuillez réessayer.");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <div style={{ maxWidth: "600px", margin: "0 auto", padding: "40px 20px" }}>
          {cardError && (
            <div style={styles.errorMessage}>
              {cardError}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show error if registration data is missing
  // In edit mode, price can be null/placeholder, but we still need registration data
  if (!registrationData || (!isEditMode && !price)) {
    return (
      <div style={styles.pageContainer}>
        <div style={{ maxWidth: "600px", margin: "0 auto", padding: "40px 20px" }}>
          <div style={styles.errorMessage}>
            {cardError || "Échec du chargement des informations de paiement. Veuillez revenir en arrière et réessayer."}
          </div>
        </div>
      </div>
    );
  }

  const total = calculateTotal();

  return (
    <div style={styles.pageContainer}>
      <div style={{ ...styles.paymentWrapper, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
        {/* Left Column - Summary */}
        <div style={styles.priceSummary}>
          <div style={styles.summaryHeader}>
            {registrationData && price && (
              <div style={styles.raceName}>{race?.name || "Race Registration"}</div>
            )}
          
          </div>
          {race && (
            <img
              src={race.image || "https://mdidlwnnrgzwjwgydotv.supabase.co/storage/v1/object/public/event/default/pexels-runffwpu-10313674.jpg"}
              alt={race?.name || "Race"}
              style={styles.raceImage}
              onError={(e) => e.target.src = "https://mdidlwnnrgzwjwgydotv.supabase.co/storage/v1/object/public/event/default/pexels-runffwpu-10313674.jpg"}
            />
          )}

          {registrationData && (
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Participant</span>
              <div style={styles.participantCell}>
                <span style={styles.detailValue}>
                  {registrationData.firstname} {registrationData.lastname}
                </span>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  style={styles.toggleButton}
                  title="Afficher les détails du prix"
                >
                  <span style={styles.toggleIcon}>{showDetails ? "▼" : "▶"}</span>
                </button>
              </div>
            </div>
          )}

          {showDetails && (
            <div style={styles.detailsSection}>

              {!isEditMode && (
                <>
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>Inscription</span>
                    <span style={styles.detailValue}>
                      {price?.price === 0 ? "Gratuit" : `€${price?.price?.toFixed(2)}`}
                    </span>
                  </div>
                  {price?.name && (
                    <div style={styles.optionSubtitle}>
                      {price.name}
                    </div>
                  )}
                </>
              )}

              {isEditMode && (
                <div style={styles.optionSubtitle}>
                  Modification de l'inscription
                </div>
              )}

              {registrationData?.extraOptions &&
                Object.keys(registrationData.extraOptions).length > 0 && (
                  <>
                    <div style={styles.optionsDivider}></div>
                    <div style={styles.optionsHeader}>Options sélectionnées</div>
                    {Object.entries(registrationData.extraOptions).map(
                      ([key, value]) => {
                        const displayChoice = Number(value?.value)
                          ? value?.value === 1
                            ? "Oui"
                            : "Non"
                          : value?.value;
                        
                        // Don't show choice if it's "Oui" or "Non"
                        const shouldShowChoice = displayChoice !== "Oui" && displayChoice !== "Non";
                        
                        return (
                          <div key={key} style={styles.optionItem}>
                            <div style={styles.detailRow}>
                              <span style={styles.detailLabel}>
                                {value?.name || key}
                              </span>
                              <span style={styles.detailValue}>
                                {value?.price ? `€${value.price.toFixed(2)}` : "Gratuit"}
                              </span>
                            </div>
                            {shouldShowChoice && (
                              <div style={styles.optionSubtitle}>
                                {displayChoice}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </>
                )}
              <div style={styles.priceDivider}></div>
              <div style={styles.priceLine}>
                <span>Sous-total</span>
                <span>€{calculateSubtotal().toFixed(2)}</span>
              </div>
              <div style={styles.priceLine}>
                <span>Frais de paiement</span>
                <span>€{calculateStripeFees().toFixed(2)}</span>
              </div>
            </div>
          )}
          <div style={styles.priceDivider}></div>
          <div style={styles.priceTotal}>
            <span>{isEditMode ? "Total à payer pour la modification" : "Total à payer"}</span>
            <span>
              {calculateTotal(true) === 0 ? "Gratuit" : `€${calculateTotal(true).toFixed(2)}`}
            </span>
          </div>
        </div>

        {/* Right Column - Payment Form */}
        <div style={styles.rightColumn}>
          <form onSubmit={handlePayment} style={styles.paymentForm}>
            <h2 style={styles.formTitle}>{isEditMode ? "Mettre à jour votre inscription" : "Payer par carte bancaire"}</h2>

            {cardError && <div style={styles.errorMessage}>{cardError}</div>}

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="you@example.com"
                style={styles.input}
                disabled={processing}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Nom du titulaire de la carte</label>
              <input
                type="text"
                name="cardName"
                value={formData.cardName}
                onChange={handleInputChange}
                placeholder="John Doe"
                style={styles.input}
                disabled={processing}
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Informations de la carte</label>
              <div 
                ref={cardElementRef}
                id="card-element" 
                style={styles.cardElement}
              ></div>
            </div>

            <button
              type="submit"
              disabled={processing || !stripe || (isEditMode && calculateTotal(true) === 0)}
              style={{
                ...styles.submitButton,
                opacity: processing || !stripe || (isEditMode && calculateTotal(true) === 0) ? 0.5 : 1,
                cursor: processing || !stripe || (isEditMode && calculateTotal(true) === 0) ? "not-allowed" : "pointer",
              }}
            >
              {processing ? (isEditMode ? "Updating..." : "Processing...") : (
                isEditMode 
                  ? calculateTotal(true) > 0 
                    ? `Payer €${calculateTotal(true).toFixed(2)}`
                    : "No changes"
                  : `Payer €${calculateTotal(true).toFixed(2)}`
              )}
            </button>

            <div style={styles.securityNote}>
              🔒 Your payment information is secure and encrypted
            </div>
          </form>

          <div style={styles.footer}>
            <span style={styles.poweredBy}>Powered by</span>
            <span style={styles.stripeLogo}>Stripe</span>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay isVisible={processing} message="Traitement du paiement..." />
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: theme.colors.gray50,
    minHeight: "86vh",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: theme.spacing.lg,
    paddingTop: theme.spacing.huge,
  },
  paymentWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    maxWidth: "900px",
    width: "100%",
    gap: theme.spacing.xxl,
    alignItems: "flex-start",
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  raceImage: {
    width: "100%",
    height: "200px",
    objectFit: "cover",
    borderRadius: theme.radius.lg,
    marginBottom: theme.spacing.lg,
  },
  toggleButton: {
    background: "none",
    border: "none",
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.gray400,
    transition: theme.transitions.base,
  },
  toggleIcon: {
    fontSize: theme.fonts.size.sm,
  },
  participantCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    justifyContent: "flex-end",
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  productIcon: {
    fontSize: theme.fonts.size.xl,
  },
  raceName: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
  },
  productPrice: {
    fontSize: theme.fonts.size.xl,
    fontWeight: theme.fonts.weight.bold,
    color: theme.colors.primary,
  },
  detailsSection: {
    marginBottom: theme.spacing.lg,
  },
  detailsTitle: {
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
    marginBottom: theme.spacing.md,
    margin: `0 0 ${theme.spacing.md} 0`,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing.md,
    fontSize: theme.fonts.size.base,
  },
  detailLabel: {
    color: theme.colors.gray500,
    fontWeight: theme.fonts.weight.medium,
  },
  detailValue: {
    color: theme.colors.gray900,
    fontWeight: theme.fonts.weight.semibold,
  },
  optionsDivider: {
    height: "1px",
    backgroundColor: theme.colors.gray200,
    margin: `${theme.spacing.md} 0`,
  },
  optionsHeader: {
    fontSize: theme.fonts.size.sm,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
    marginBottom: theme.spacing.xs,
  },
  priceSummary: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.radius.md,
    marginTop: theme.spacing.md,
  },
  priceLine: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing.md,
    fontSize: theme.fonts.size.base,
  },
  priceDivider: {
    height: "1px",
    backgroundColor: theme.colors.gray200,
    margin: `${theme.spacing.md} 0`,
  },
  priceTotal: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: theme.fonts.size.md,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
  },
  rightColumn: {
    paddingTop: theme.spacing.lg,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  paymentForm: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing.lg,
  },
  formTitle: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
    marginBottom: theme.spacing.lg,
    margin: `0 0 ${theme.spacing.lg} 0`,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing.md,
  },
  label: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.gray700,
    fontWeight: theme.fonts.weight.semibold,
    marginBottom: theme.spacing.sm,
  },
  input: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.gray300}`,
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.base,
    fontFamily: theme.fonts.family.primary,
    backgroundColor: theme.colors.white,
    transition: theme.transitions.base,
  },
  cardElement: {
    padding: `${theme.spacing.lg} ${theme.spacing.md}`,
    border: `1px solid ${theme.colors.gray300}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.white,
    minHeight: "55px",
    height: "55px",
    alignItems: "center",
  },
  submitButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.light,
    color: theme.colors.textInverted,
    border: "none",
    borderRadius: theme.radius.md,
    fontSize: theme.fonts.size.base,
    fontWeight: theme.fonts.weight.semibold,
    cursor: "pointer",
    marginBottom: theme.spacing.md,
    transition: theme.transitions.base,
  },
  errorMessage: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.errorBg,
    color: theme.colors.error,
    borderRadius: theme.radius.md,
    marginBottom: theme.spacing.md,
    fontSize: theme.fonts.size.base,
    border: `1px solid ${theme.colors.error}`,
  },
  securityNote: {
    fontSize: theme.fonts.size.sm,
    color: theme.colors.gray500,
    textAlign: "center",
    marginTop: theme.spacing.md,
  },
  footer: {
    marginTop: theme.spacing.md,
    textAlign: "center",
    fontSize: theme.fonts.size.sm,
    color: theme.colors.gray400,
  },
  poweredBy: {
    marginRight: theme.spacing.xs,
  },
  stripeLogo: {
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.gray900,
  },
  optionItem: {
    marginBottom: theme.spacing.md,
  },
  optionSubtitle: {
    fontSize: theme.fonts.size.sm,
    marginTop: `-${theme.spacing.sm}`,
    color: theme.colors.gray400,
    fontStyle: "italic",
  },
};
